import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    gradeLevel: '',
    school: '',
    city: '',
    country: '',
  });

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    workshopReminders: true,
    deadlineAlerts: true,
    weeklyDigest: false,
  });

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  const gradeLevelOptions = [
    { value: '9', label: 'Grade 9' },
    { value: '10', label: 'Grade 10' },
    { value: '11', label: 'Grade 11' },
    { value: '12', label: 'Grade 12' },
    { value: 'graduate', label: 'Recent Graduate' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login-register');
      return;
    }
    loadProfileData();
  }, [isAuthenticated, user?.id]);

  const loadProfileData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Load from profiles table
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Load from personal_info table
      const { data: personalInfo } = await supabase
        .from('personal_info')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setProfileData({
        displayName: profile?.display_name || personalInfo?.first_name || '',
        email: user.email || '',
        phoneNumber: profile?.phone_number || '',
        gradeLevel: personalInfo?.grade || profile?.grade_level || '',
        school: personalInfo?.school || '',
        city: personalInfo?.city || '',
        country: personalInfo?.country || '',
      });

      setPreferences({
        emailNotifications: profile?.email_notifications ?? true,
        workshopReminders: profile?.workshop_reminders ?? true,
        deadlineAlerts: profile?.deadline_alerts ?? true,
        weeklyDigest: profile?.weekly_digest ?? false,
      });
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  

  const handlePreferenceChange = (name) => {
    setPreferences((prev) => ({ ...prev, [name]: !prev[name] }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      setSaving(true);
      setErrorMessage('');

      console.log('Saving profileData:', profileData);

      // Update profiles table and read response for debugging
      const { data: profileUpsertData, error: profileError } = await supabase
        .from('profiles')
        .upsert(
          {
            id: user.id,
            display_name: profileData.displayName,
            phone_number: profileData.phoneNumber || null,
            grade_level: profileData.gradeLevel,
            email_notifications: preferences.emailNotifications,
            workshop_reminders: preferences.workshopReminders,
            deadline_alerts: preferences.deadlineAlerts,
            weekly_digest: preferences.weeklyDigest,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        )
        .select()
        .single();

      console.log('profiles upsert response:', { profileUpsertData, profileError });
      if (profileError) throw profileError;

      // Fallback: ensure phone_number is stored (some RLS/policy/edge cases may drop it on upsert)
      try {
        const shouldPhone = profileData.phoneNumber || null;
        const { data: phoneUpdateData, error: phoneUpdateError } = await supabase
          .from('profiles')
          .update({ phone_number: shouldPhone })
          .eq('id', user.id)
          .select()
          .single();

        console.log('profiles phone update response:', { phoneUpdateData, phoneUpdateError });
        if (phoneUpdateError) {
          // non-fatal: log and surface a user-visible message
          console.error('Phone update failed:', phoneUpdateError);
          setErrorMessage(prev => prev || 'Profile saved — but phone number may not have been stored. Check console.');
        }
      } catch (err) {
        console.error('Unexpected error updating phone number:', err);
        setErrorMessage(prev => prev || 'Profile saved — but phone number may not have been stored. Check console.');
      }

      // Update personal_info table
      const { data: personalUpsertData, error: personalError } = await supabase
        .from('personal_info')
        .upsert(
          {
            user_id: user.id,
            first_name: profileData.displayName,
            phone: profileData.phoneNumber || null,
            grade: profileData.gradeLevel,
            school: profileData.school,
            city: profileData.city,
            country: profileData.country,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        )
        .select()
        .single();

      console.log('personal_info upsert response:', { personalUpsertData, personalError });
      if (personalError) throw personalError;

      // Try to record marketing contact and report any errors (non-fatal)
      try {
        const { data: mcData, error: mcError } = await supabase
          .from('marketing_contacts')
          .insert([
            {
              user_id: user.id,
              full_name: profileData.displayName || null,
              phone_prefix: null,
              phone_number: profileData.phoneNumber || null,
              raw_phone: profileData.phoneNumber || null,
            },
          ])
          .select()
          .single();

        if (mcError) {
          console.error('Failed to record marketing contact:', mcError);
          setErrorMessage(prev => prev || 'Profile saved — but failed to record marketing contact. Check console.');
        } else {
          console.log('Recorded marketing contact:', mcData);
          try {
            const { data: mcCheck, error: mcCheckError } = await supabase
              .from('marketing_contacts')
              .select('*')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false });

            if (mcCheckError) {
              console.error('Failed to query marketing_contacts after insert:', mcCheckError);
              // show a visible alert so the user can see a failure even if console hidden
              alert('Marketing contact insert reported success but subsequent read failed. Check console for details.');
            } else {
              console.log('marketing_contacts for user:', mcCheck);
              alert(`Marketing contacts found for this account: ${mcCheck?.length || 0}. Most recent: ${JSON.stringify(mcCheck?.[0] || {})}`);
            }
          } catch (err) {
            console.error('Unexpected error checking marketing_contacts:', err);
          }
        }
      } catch (err) {
        console.error('Failed to record marketing contact (unexpected):', err);
        setErrorMessage(prev => prev || 'Profile saved — but failed to record marketing contact. Check console.');
      }

      setSuccessMessage('Profile settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setErrorMessage('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setPasswordLoading(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      // Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.currentPassword,
      });

      if (signInError) {
        setPasswordMessage({ type: 'error', text: 'Current password is incorrect' });
        setPasswordLoading(false);
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (updateError) throw updateError;

      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordMessage({ type: '', text: '' });
      }, 2000);
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordMessage({ type: 'error', text: err.message || 'Failed to change password' });
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen app-shell">
        <Header />
        <main className="pt-24 pb-14 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-3" />
              <p className="text-muted-foreground">Loading settings...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Profile Settings - UniSteps</title>
        <meta name="description" content="Manage your profile and preferences" />
      </Helmet>

      <div className="min-h-screen app-shell">
        <Header />

        <main className="pt-24 pb-14 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div className="surface-card rounded-2xl p-6 lg:p-8 overflow-hidden relative fade-up">
              <div className="absolute inset-0 tile-grid opacity-60" aria-hidden="true" />
              <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-2">
                  <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Your account</p>
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground headline-serif">
                    Profile Settings
                  </h1>
                  <p className="text-muted-foreground max-w-3xl">
                    Manage your personal information and notification preferences
                  </p>
                </div>
                <Button onClick={() => navigate('/student-dashboard')} variant="outline" iconName="ArrowLeft">
                  Back
                </Button>
              </div>
            </div>

            {/* Messages */}
            {successMessage && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="CheckCircle2" size={20} className="text-success" />
                <p className="text-sm text-success">{successMessage}</p>
              </div>
            )}
            {errorMessage && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-sm text-error">{errorMessage}</p>
              </div>
            )}

            {/* Profile Information */}
            <div className="bg-card border border-border rounded-lg p-6 space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-1">Profile Information</h2>
                <p className="text-sm text-muted-foreground">Update your personal details</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Display Name"
                  name="displayName"
                  value={profileData.displayName}
                  onChange={handleProfileChange}
                  placeholder="How should we call you?"
                  iconName="User"
                />
                <Input
                  label="Email"
                  name="email"
                  value={profileData.email}
                  disabled
                  iconName="Mail"
                />
                <Input
                  label="Phone Number (Optional)"
                  name="phoneNumber"
                  value={profileData.phoneNumber}
                  onChange={handleProfileChange}
                  placeholder="+1 234 567 8900"
                  iconName="Phone"
                />
                <Select
                  label="Grade Level"
                  value={profileData.gradeLevel}
                  onChange={(value) =>
                    setProfileData((prev) => ({ ...prev, gradeLevel: value }))
                  }
                  options={gradeLevelOptions}
                  placeholder="Select your grade"
                />
                <Input
                  label="School (Optional)"
                  name="school"
                  value={profileData.school}
                  onChange={handleProfileChange}
                  placeholder="Your school name"
                  iconName="School"
                />
                <Input
                  label="City (Optional)"
                  name="city"
                  value={profileData.city}
                  onChange={handleProfileChange}
                  placeholder="Your city"
                  iconName="MapPin"
                />
                <Input
                  label="Country (Optional)"
                  name="country"
                  value={profileData.country}
                  onChange={handleProfileChange}
                  placeholder="Your country"
                  iconName="Globe"
                />
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="bg-card border border-border rounded-lg p-6 space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-1">Notification Preferences</h2>
                <p className="text-sm text-muted-foreground">
                  Choose what updates you want to receive
                </p>
              </div>

              <div className="space-y-4">
                {[
                  {
                    name: 'emailNotifications',
                    label: 'Email Notifications',
                    description: 'Receive important updates via email',
                  },
                  {
                    name: 'workshopReminders',
                    label: 'Workshop Reminders',
                    description: 'Get notified before your registered workshops',
                  },
                  {
                    name: 'deadlineAlerts',
                    label: 'Deadline Alerts',
                    description: 'Reminders for application deadlines',
                  },
                  {
                    name: 'weeklyDigest',
                    label: 'Weekly Digest',
                    description: 'Summary of your activity and recommendations',
                  },
                ].map((pref) => (
                  <div
                    key={pref.name}
                    className="flex items-start justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{pref.label}</p>
                      <p className="text-sm text-muted-foreground">{pref.description}</p>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange(pref.name)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences[pref.name] ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences[pref.name] ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-card border border-border rounded-lg p-6 space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-1">Security Settings</h2>
                <p className="text-sm text-muted-foreground">
                  Manage your password and account security
                </p>
              </div>

              {/* Email Display */}
              <div className="p-4 rounded-lg bg-muted/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email Address</p>
                    <p className="font-medium text-foreground">{profileData.email}</p>
                  </div>
                  <Icon name="Mail" size={20} className="text-muted-foreground" />
                </div>
              </div>

              {/* Change Password Section */}
              <div className="border border-border/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-medium text-foreground">Password</p>
                    <p className="text-sm text-muted-foreground">
                      {showChangePassword ? 'Update your account password' : 'Manage your account password'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowChangePassword(!showChangePassword);
                      setPasswordMessage({ type: '', text: '' });
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                  >
                    {showChangePassword ? 'Cancel' : 'Change Password'}
                  </Button>
                </div>

                {showChangePassword && (
                  <form onSubmit={handlePasswordChange} className="space-y-4 mt-4 pt-4 border-t border-border/50">
                    {/* Security Warning */}
                    <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 flex gap-3">
                      <Icon name="AlertCircle" size={18} className="text-warning flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Keep your password secure</p>
                        <p className="text-xs text-muted-foreground">Never share your password with anyone</p>
                      </div>
                    </div>

                    <Input
                      label="Current Password"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      placeholder="Enter your current password"
                      iconName="Lock"
                    />

                    <Input
                      label="New Password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      placeholder="Enter new password (min 8 characters)"
                      iconName="KeyRound"
                    />

                    <Input
                      label="Confirm New Password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Confirm new password"
                      iconName="KeyRound"
                    />

                    {/* Password Message */}
                    {passwordMessage.text && (
                      <div className={`p-3 rounded-lg flex gap-3 ${
                        passwordMessage.type === 'error' 
                          ? 'bg-error/10 border border-error/20' 
                          : 'bg-success/10 border border-success/20'
                      }`}>
                        <Icon 
                          name={passwordMessage.type === 'error' ? 'AlertCircle' : 'CheckCircle2'} 
                          size={18} 
                          className={passwordMessage.type === 'error' ? 'text-error' : 'text-success'} 
                        />
                        <span className={`text-sm ${passwordMessage.type === 'error' ? 'text-error' : 'text-success'}`}>
                          {passwordMessage.text}
                        </span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={passwordLoading}
                      iconName={passwordLoading ? 'Loader2' : 'Lock'}
                      className="w-full"
                    >
                      {passwordLoading ? 'Updating Password...' : 'Update Password'}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={() => navigate('/student-dashboard')}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} iconName={saving ? 'Loader2' : 'Save'}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ProfileSettings;
